/* eslint-disable no-restricted-globals */

import React from 'react';
import PropTypes from 'prop-types';
import Button from '@tds/core-button';
import FlexGrid from '@tds/core-flex-grid';
import Paragraph from '@tds/core-paragraph';
import Strong from '@tds/core-strong';
import { Success, Time, ThumbsUp, Attention } from '@tds/core-decorative-icon';
import { Chip, ChipIcon, ChipLabel, LinkButton, SpacedRow, Title, WidgetContainer, ItalicDescription } from '../css';

const chipStatusColorMap = {
    'auto-approved': {
        primary: '#D4EDDA',
        secondary: '#155724',
        text: 'Auto-Approved',
        icon: <ThumbsUp size={12}/>
    },
    'approved': {
        primary: '#D4EDDA',
        secondary: '#155724',
        text: 'Approved',
        icon: <ThumbsUp size={12}/>
    },
    'pending-approval': {
        primary: '#FFF3CD',
        secondary: '#856404',
        text: 'Pending Approval',
        icon: <Time size={12}/>
    },
    'new': {
        primary: '#FFF3CD',
        secondary: '#856404',
        text: 'Pending Approval',
        icon: <Time size={12}/>
    },
    'rejected': {
        primary: '#F8D7DA',
        secondary: '#721C24',
        text: 'Rejected',
        icon: <Attention size={12}/>
    },
}

const isValueValid = (val) => {
  return (!isNaN(val) && !!val);
}

const DiscountItem = ({
    id,
    status,
    monthly,
    onetime,
    approvedMonthly,
    approvedOnetime,
    overrideMonthly,
    overrideOnetime,
    onOverride,
    onApply,
    applied,
    term,
    commentFromApprover,
    comment,
    formTerm,
    requestedForManagedITServicesValue,
    requestedForOnboardingFeeValue,
    managedItServicesTotal,
    onboardingFee
  }) => {
    const discountStatus = status.toLowerCase();
    const isDiscountCompatibleWithOrder = 
      (requestedForManagedITServicesValue === +(managedItServicesTotal || 0).toFixed(2))
      && (requestedForOnboardingFeeValue === +(onboardingFee || 0).toFixed(2));

    return (
      <WidgetContainer applied={applied}>
        <FlexGrid>
          <FlexGrid.Row>
            <FlexGrid.Col xs={9}>
              <Title>Discount ID: {id}</Title>
            </FlexGrid.Col>
            <FlexGrid.Col xs={3}>
              {applied && <Success size={28} />}
            </FlexGrid.Col>
          </FlexGrid.Row>
          <FlexGrid.Row>
            <FlexGrid.Col xs={12}>
              {chipStatusColorMap[discountStatus] && (
                <Chip
                  size="small"
                  bgColor={chipStatusColorMap[discountStatus].primary}
                  textColor={chipStatusColorMap[discountStatus].secondary}
                >
                  <ChipLabel hasIcon>
                    {chipStatusColorMap[discountStatus].text}
                  </ChipLabel>
                  <ChipIcon size={12}>
                    {chipStatusColorMap[discountStatus].icon}
                  </ChipIcon>
                </Chip>
              )}
              <Chip size="small">
                <ChipLabel>{term} term</ChipLabel>
                {term !== formTerm && (
                  <ChipIcon size={12}>
                    <Attention size={12} />
                  </ChipIcon>
                )}
              </Chip>
              {applied && (
                <Chip size="small" textColor="#4B286D">
                  <ChipLabel hasIcon>Discount Applied to Form</ChipLabel>
                  <ChipIcon size={12}>
                    <Success size={12} />
                  </ChipIcon>
                </Chip>
              )}
            </FlexGrid.Col>
          </FlexGrid.Row>
          <SpacedRow>
            <>
              <>
                <FlexGrid.Row>
                  <FlexGrid.Col xs={12}>
                    <Paragraph>
                      <Strong>Requested Monthly Discount:</Strong> ${monthly}
                    </Paragraph>
                  </FlexGrid.Col>
                  <FlexGrid.Col xs={12}>
                    <Paragraph>
                      <Strong>Requested One-Time Discount:</Strong> ${onetime}
                    </Paragraph>
                  </FlexGrid.Col>
                </FlexGrid.Row>
              </>
              <FlexGrid.Row>
                {isValueValid(approvedMonthly) && (
                  <FlexGrid.Col xs={12}>
                    <Paragraph>
                      <Strong>Approved Monthly Discount: </Strong>$
                      {approvedMonthly}
                    </Paragraph>
                  </FlexGrid.Col>
                )}
                {isValueValid(approvedOnetime) && (
                  <FlexGrid.Col xs={12}>
                    <Paragraph>
                      <Strong>Approved One-Time Discount: </Strong>$
                      {approvedOnetime}
                    </Paragraph>
                  </FlexGrid.Col>
                )}
              </FlexGrid.Row>
              <FlexGrid.Row>
                {isValueValid(+overrideMonthly) && (
                  <FlexGrid.Col xs={12}>
                    <Paragraph>
                      <Strong>Reduced Monthly Discount: </Strong>$
                      {overrideMonthly}
                    </Paragraph>
                  </FlexGrid.Col>
                )}
                {isValueValid(+overrideOnetime) && (
                  <FlexGrid.Col xs={12}>
                    <Paragraph>
                      <Strong>Reduced One-Time Discount: </Strong>$
                      {overrideOnetime}
                    </Paragraph>
                  </FlexGrid.Col>
                )}
              </FlexGrid.Row>
              {commentFromApprover &&
                <FlexGrid.Row>
                  <FlexGrid.Col xs={12}>
                    <Paragraph>
                      <Strong>Approver Comment: </Strong>
                    </Paragraph>
                  </FlexGrid.Col>
                  <FlexGrid.Col xs={12}>
                    <Paragraph>
                      <ItalicDescription>{commentFromApprover}</ItalicDescription>
                    </Paragraph>
                  </FlexGrid.Col>
                </FlexGrid.Row>
              }
            </>
          </SpacedRow>

          <SpacedRow>
            <FlexGrid.Row style={{ marginTop: 20 }}>
              <FlexGrid.Col xs={4}>
                {['approved', 'auto-approved'].includes(discountStatus) && term === formTerm && isDiscountCompatibleWithOrder && (
                  <LinkButton onClick={onOverride}>
                    Reduce Discount
                  </LinkButton>
                )}
              </FlexGrid.Col>
              {term === formTerm && !['rejected'].includes(discountStatus) && isDiscountCompatibleWithOrder && (
                <FlexGrid.Col xs={8}>
                  <Button
                    onClick={() => onApply(applied ? 'unApply' : 'apply')}
                    variant={applied ? 'secondary' : 'primary'}
                  >
                    {applied ? 'Unapply Discount' : 'Apply Discount'}
                  </Button>
                </FlexGrid.Col>
              )}
            </FlexGrid.Row>
          </SpacedRow>
        </FlexGrid>
      </WidgetContainer>
    );
  };

DiscountItem.propTypes = {
    id: PropTypes.string.isRequired,
    status: PropTypes.oneOf(['approved', 'underApproval', 'rejected']).isRequired,
    monthly: PropTypes.number.isRequired,
    onetime: PropTypes.number.isRequired,
    approvedMonthly: PropTypes.number.isRequired,
    approvedOnetime: PropTypes.number.isRequired,
    overrideMonthly: PropTypes.number.isRequired,
    overrideOnetime: PropTypes.number.isRequired,
    onOverride: PropTypes.func.isRequired,
    onApply: PropTypes.func.isRequired,
    applied: PropTypes.bool.isRequired,
    term: PropTypes.string.isRequired,
    formTerm: PropTypes.string.isRequired,
    comment: PropTypes.string.isRequired,
    commentFromApprover: PropTypes.string.isRequired,
    requestedForManagedITServicesValue: PropTypes.number.isRequired,
    requestedForOnboardingFeeValue: PropTypes.number.isRequired,
    managedItServicesTotal: PropTypes.number.isRequired,
    onboardingFee: PropTypes.number.isRequired,
};

  export default DiscountItem;